import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X, ShoppingCart } from "lucide-react";
import { useSeoMeta } from "@/lib/seo";

const NAV = [
  { to: "/presentation", label: "Présentation", testid: "nav-presentation" },
  { to: "/tarifs",       label: "Tarif",        testid: "nav-tarifs" },
  { to: "/demo",         label: "Démo",         testid: "nav-demo" },
  { to: "/contact",      label: "Contact",      testid: "nav-contact" },
];

const PAGE_TITLES = {
  "/": "",
  "/presentation": "Présentation",
  "/tarifs": "Tarif",
  "/demo": "Démo",
  "/contact": "Contact",
  "/checkout/success": "Paiement",
  "/mentions-legales": "Mentions légales",
  "/cgv": "Conditions générales de vente",
  "/confidentialite": "Politique de confidentialité",
};

export default function PublicHeader() {
  const [theme, setTheme] = useState(() => localStorage.getItem("nk-landing-theme") || "light");
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Inject global SEO + page title in <head>
  useSeoMeta(PAGE_TITLES[location.pathname] || "");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("nk-landing-theme", theme);
  }, [theme]);

  // Ferme le menu mobile à chaque changement de route
  const pathname = location.pathname;
  useEffect(() => { setOpen(false); }, [pathname]);

  const linkCls = ({ isActive }) =>
    `text-[14px] tracking-normal transition-colors hover:text-primary ${isActive ? "text-primary" : "text-foreground"}`;
  const navStyle = { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 500 };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border" data-testid="public-header">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="public-header-logo">
          <div className="h-9 w-9 rounded-[10px] bg-primary text-primary-foreground flex items-center justify-center font-black text-lg">W</div>
          <div className="leading-tight">
            <div className="font-extrabold text-sm">WiLink</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hidden sm:block">Portail d&apos;entreprise</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="public-nav-desktop" style={navStyle}>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.testid}>{n.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            className="h-9 w-9 rounded-md border border-border flex items-center justify-center hover:bg-accent"
            aria-label="Thème"
            data-testid="public-theme-toggle"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link
            to="/tarifs"
            className="h-9 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium hidden sm:inline-flex items-center gap-2"
            data-testid="public-header-order"
          >
            <ShoppingCart className="h-4 w-4" /> Commander
          </Link>
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden h-9 w-9 rounded-md border border-border flex items-center justify-center"
            aria-label="Menu"
            data-testid="public-mobile-toggle"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-border bg-background px-6 py-4 flex flex-col gap-4" data-testid="public-nav-mobile" style={navStyle}>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`${n.testid}-mobile`}>{n.label}</NavLink>
          ))}
          <Link to="/tarifs" className="text-[14px] text-primary" data-testid="public-mobile-order">Commander →</Link>
        </nav>
      )}
    </header>
  );
}
